import React, {useState} from 'react';
import moment from 'moment';
import groupBy from 'lodash/groupBy';
import sortBy from 'lodash/sortBy';

import Box from '@mui/material/Box';
import SearchIcon from '@mui/icons-material/Search';
import BackIcon from '@mui/icons-material/ArrowBackIos';
import ForwardIcon from '@mui/icons-material/ArrowForwardIos';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
// import FilterIcon from '@mui/icons-material/FilterList';

import {
  useGetProviderAvailabilitiesQuery
} from '../../redux/services/providerAvailabilityApi';
import useDebouncedState from '../../hooks/useDebouncedState';
import {
  calculateNextAvailabilityDate
} from '../../helpers/scheduling';
import ChoiceField from '../common/ChoiceField';
import CustomTextField from '../common/CustomTextField';
import DayPicker from '../common/DayPicker';
import SchedulingProviderItem from './SchedulingProviderItem';

const parseQuery = ({
  searchMethod, 
  location, 
  serviceCategory, 
  date,
  searchText,
}) => {
  if (searchMethod === 'BY_PROVIDER') {
    return {
      lat: location.lat,
      lon: location.lon,
      distance: location.distance || 30,
      service_category: serviceCategory,
      search_text: searchText,
    };
  }
  const dt = moment(date);
  return {
    lat: location.lat,
    lon: location.lon, 
    distance: location.distance || 30,
    service_category: serviceCategory,
    day: dt.format('ddd').toUpperCase(),
    start_minute__gte: 1,
    end_minute__lte: 23 * 60 + 59,
  };
};

const useDefaultDate = ({searchMethod, location, serviceCategory}) => {
  const availabilityQuery = useGetProviderAvailabilitiesQuery({
    lat: location.lat,
    lon: location.lon,
    distance: location.distance || 30,
    service_category: serviceCategory,
  }, {skip: searchMethod !== 'BY_AVAILABILITY'});
  if (searchMethod !== 'BY_AVAILABILITY') {
    return {defaultDate: new Date(), isLoading: false};
  }
  console.log('DEFAULT_DT_QUERY', availabilityQuery);
  const isLoading = availabilityQuery.isLoading;
  const availabilitiesWithDates = (availabilityQuery?.data?.data || []).map(
    a => ({...a, next_date: calculateNextAvailabilityDate(a)})
  );
  const nextAvailability = sortBy(
    availabilitiesWithDates,
    a => -1 * (new Date(a.next_date)).valueOf()
  );
  const defaultDate = new Date(nextAvailability?.[0]?.next_date) || moment().toDate();
  return {defaultDate, isLoading};
};

const PatientSchedulingProviderForm = ({
  onSubmit,
  searchMethod = 'BY_AVAILABILITY',
  location = null,
  serviceCategory = null,
  stepNum = 3,
  onChangeSearchMethod,
  defaultDate = null,
}) => {
  const [searchText, setSearchText] = useState('');
  const [date, setDate] = useState(defaultDate);
  useDebouncedState(searchText, setSearchText, 500);
  const title = searchMethod === 'BY_PROVIDER' ? 'Find a Provider' : 'Pick a Time';
  const subtitle = (
    searchMethod === 'BY_PROVIDER' 
    ? 'Find a provider who fits your needs' 
    : 'Find a provider who fits your schedule'
  );
  const query = parseQuery({
    searchMethod, 
    location, 
    serviceCategory, 
    date, 
    searchText,
  });
  const paQuery = useGetProviderAvailabilitiesQuery(
    query, 
    {skip: searchMethod === 'BY_AVAILABILITY' && !date}
  );
  const availabilities = paQuery?.data?.data || [];
  const providers = Object.entries(groupBy(availabilities, 'provider.id'))
    .map(([k, v]) => ({...v[0].provider, availabilities: v}));
  return (
    <Box>
      <Stack spacing={2} alignItems='center'>
        <Stack spacing={0.5} alignItems='left' pl={2} pr={2}>
          <Stack pb={1} pt={1}>
            <Typography variant='body2' color='text.muted'>
              Not finding the right time? Search by Providers to coordinate scheduling directly with a provider near you.
            </Typography>
            <Stack direction='row' justifyContent='center' pt={.5}>
              <ChoiceField
                defaultValue={searchMethod}
                options={[
                  {label: 'Find by Availability', value: 'BY_AVAILABILITY'},
                  {label: 'Search by Providers', value: 'BY_PROVIDER'},
                ]}
                onChange={(e, val) => onChangeSearchMethod(e, val)}
              />
            </Stack>
          </Stack>
          <Typography variant='h2' pt={2}>{title}</Typography>
          <Typography>{subtitle}</Typography>
        </Stack>
        {searchMethod === 'BY_AVAILABILITY' && 
          <>
            {/* <Stack 
              direction='row' 
              alignItems='center' 
              justifyContent='space-between'
            >
              <Typography variant='h3'>
                Pick a Day to View Available Appointments
              </Typography>

            </Stack> */}
            <Stack direction='row' justifyContent='center' width='100%'>
              <Box sx={{minWidth: '300px'}}>
                <Stack
                  direction='row' 
                  justifyContent='space-between'
                  alignItems='center' 
                  spacing={1}
                  flexGrow={1}
                  width='100%'
                >
                  <IconButton
                    size='small'
                    disabled={moment(date).startOf('week').isBefore(moment())}
                    onClick={() => setDate(moment(date).subtract(1, 'week').toDate())}
                  >
                    <BackIcon fontSize='inherit' />
                  </IconButton>
                  {moment(date).startOf('week').format('MMM D') + ' - ' + moment(date).endOf('week').format('MMM D')}
                  <IconButton
                    size='small'
                    onClick={() => setDate(moment(date).add(1, 'week').toDate())}
                  >
                    <ForwardIcon fontSize='inherit' />
                  </IconButton>
                </Stack>
              </Box>
            </Stack>
            <DayPicker 
              date={date} 
              spacing={0.5}
              onChange={(e, date) => setDate(date)}
              availabilities={availabilities} 
              disabledDays={['SAT', 'SUN']}
              allowPastDays={false}
            />
          </>
        }
        {
          searchMethod === 'BY_PROVIDER' && 
          <>
            <Stack direction='row' alignItems='center' spacing={1}>
              <Typography variant='h3'>Providers Near You</Typography>
            </Stack>
            <Stack direction='row' alignItems='center' spacing={1}>
              <CustomTextField
                placeholder='Search by name, specialty, or condition'
                fullWidth
                style={{minWidth: '400px'}}
                InputProps={{
                  startAdornment: <SearchIcon color='primary' />
                }}
                onChange={(e, val) => setSearchText(val)}
              />
              {/* <Button
                color='light'
                variant='contained'
                size='small'
                startIcon={<FilterIcon />}
              >
                Filter
              </Button> */}
            </Stack>
          </>
        }
        <Stack spacing={2} padding={2} style={{width: '100%'}}>
          {
            providers.map(p => 
              <SchedulingProviderItem 
                key={p.id} 
                provider={p}
                availabilities={p.availabilities}
                serviceCategory={serviceCategory}
                location={location}
                day={moment(date).format('ddd').toUpperCase()}
                date={date}
                onTimeSelect={(e, data) => onSubmit(e, data)}
                onScheduleManually={e => onSubmit(e, {
                  providerId: p.id,
                  scheduleManually: true,
                  isoString: null,
                  sessionTypeId: null,
                  providerAvailabilityId: null,
                })}
                hideSlots={searchMethod === 'BY_PROVIDER'}
              />
            )
          }
          {
            paQuery.isSuccess && providers.length === 0 &&
            <Typography variant='h3' align='center' color='text.muted' py={4}>
              We apologize, no direct booking times are available based on your 
              search criteria. Please search for a provider and coordinate 
              with them directly to schedule. You can also reach us at 
              <a href="mailto:info@movement-x.com">info@movement-x.com</a> for assistance.
            </Typography>
          }
        </Stack>
      </Stack>
    </Box>
  );
};

const PatientSchedulingProviderFormContainer = props => {
  const {defaultDate, isLoading} = useDefaultDate(props);
  const childProps = {
    ...props,
    defaultDate,
  };
  if (isLoading) return null;
  return <PatientSchedulingProviderForm {...childProps} />;
};

export default PatientSchedulingProviderFormContainer;